/**
 * CloseRateCard — shows won/lost close-rate percentages grouped by score bucket.
 * Sits directly below the KPI row on the Dashboard.
 */
import React, { useEffect, useState } from 'react';
import { ReportsAPI } from '../services/api';

const BUCKET_LABELS = {
    '9-10': 'Hot (9–10)',
    '7-8':  'Warm (7–8)',
    '5-6':  'Lukewarm (5–6)',
    '1-4':  'Cold (1–4)',
};

const BUCKET_COLORS = {
    '9-10': 'bg-emerald-500',
    '7-8':  'bg-green-400',
    '5-6':  'bg-yellow-400',
    '1-4':  'bg-gray-300',
};

export default function CloseRateCard({ refreshKey = 0 }) {
    const [buckets, setBuckets] = useState(null);
    const [error, setError]     = useState(null);

    useEffect(() => {
        ReportsAPI.closeRate()
            .then((d) => setBuckets(d.buckets))
            .catch(() => setError('Could not load close-rate data.'));
    }, [refreshKey]);

    const hasData = buckets && buckets.some((b) => b.total > 0);

    return (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">Close rate by score bucket</h2>

            {error && (
                <p className="text-xs text-red-600">{error}</p>
            )}

            {!error && !buckets && (
                <div className="space-y-2">
                    {[1, 2, 3, 4].map((n) => (
                        <div key={n} className="h-4 bg-gray-100 rounded animate-pulse" />
                    ))}
                </div>
            )}

            {buckets && !hasData && (
                <p className="text-xs text-gray-500">
                    Mark leads as won or lost to see your close rate by score bucket.
                </p>
            )}

            {hasData && (
                <table className="w-full text-xs">
                    <thead>
                        <tr className="text-gray-400 text-left">
                            <th className="pb-1 font-medium w-28">Bucket</th>
                            <th className="pb-1 font-medium text-right">Total</th>
                            <th className="pb-1 font-medium text-right">Won</th>
                            <th className="pb-1 font-medium text-right">Lost</th>
                            <th className="pb-1 font-medium text-right pr-1">Rate</th>
                            <th className="pb-1 w-24"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                        {buckets.map((b) => (
                            <tr key={b.bucket} className="py-1">
                                <td className="py-1 font-medium text-gray-700">{BUCKET_LABELS[b.bucket] ?? b.bucket}</td>
                                <td className="py-1 text-right text-gray-500">{b.total}</td>
                                <td className="py-1 text-right text-green-700">{b.won}</td>
                                <td className="py-1 text-right text-red-600">{b.lost}</td>
                                <td className="py-1 text-right font-semibold text-gray-800 pr-2">
                                    {b.close_rate !== null ? `${b.close_rate}%` : '—'}
                                </td>
                                <td className="py-1">
                                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                        <div
                                            className={`h-full rounded-full ${BUCKET_COLORS[b.bucket] ?? 'bg-gray-400'}`}
                                            style={{ width: `${b.close_rate ?? 0}%` }}
                                        />
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}
